import axios from 'axios';
import {
  SupportConversation,
  SupportMessage,
  CreateConversationRequest,
  SendMessageRequest
} from '../types/support';

// Normalize API base (strip trailing /api) to avoid double /api in path
const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001').replace(/\/api\/?$/, '').replace(/\/$/, '');
const baseUrl = `${apiBase}/api/support`;

// Create a separate axios instance for support service (no auth headers needed for guest users)
const supportApi = axios.create({
  baseURL: baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

class SupportService {
  /**
   * Create or get active conversation
   */
  async createOrGetConversation(request: CreateConversationRequest): Promise<SupportConversation> {
    try {
      const response = await supportApi.post<{ conversation: SupportConversation }>(
        '/conversation/create',
        request
      );
      return response.data.conversation;
    } catch (error: any) {
      console.error('Error creating/getting conversation:', error);
      if (error.response) {
        console.error('Response error:', error.response.data);
        throw new Error(error.response.data?.error || error.response.data?.message || 'Failed to create/get conversation');
      }
      throw error;
    }
  }

  /**
   * Get conversation with messages
   */
  async getConversation(conversationId: number, userId: string): Promise<{ conversation: SupportConversation; messages: SupportMessage[] }> {
    try {
      const response = await supportApi.post<{ conversation: SupportConversation; messages: SupportMessage[] }>(
        `/conversation/${conversationId}`,
        { userId, isAdmin: false }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error getting conversation:', error);
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.message || 'Failed to get conversation');
      }
      throw error;
    }
  }

  /**
   * Get user's conversations
   */
  async getUserConversations(userId: string): Promise<SupportConversation[]> {
    try {
      const response = await supportApi.post<{ conversations: SupportConversation[] }>(
        '/conversations/user',
        { userId }
      );
      return response.data.conversations;
    } catch (error: any) {
      console.error('Error getting user conversations:', error);
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.message || 'Failed to get conversations');
      }
      throw error;
    }
  }

  /**
   * Send message
   */
  async sendMessage(request: SendMessageRequest): Promise<SupportMessage> {
    try {
      const response = await supportApi.post<{ message: SupportMessage }>(
        '/message/send',
        request
      );
      return response.data.message;
    } catch (error: any) {
      console.error('Error sending message:', error);
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.message || 'Failed to send message');
      }
      throw error;
    }
  }

  /**
   * Mark messages as read
   */
  async markAsRead(conversationId: number, senderType: 'user' | 'admin'): Promise<void> {
    try {
      await supportApi.post('/message/mark-read', {
        conversationId,
        senderType
      });
    } catch (error: any) {
      console.error('Error marking messages as read:', error);
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.message || 'Failed to mark messages as read');
      }
      throw error;
    }
  }

  /**
   * Get unread count
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const response = await supportApi.post<{ unreadCount: number }>(
        '/unread-count',
        { userId, isAdmin: false }
      );
      return response.data.unreadCount;
    } catch (error: any) {
      console.error('Error getting unread count:', error);
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.message || 'Failed to get unread count');
      }
      throw error;
    }
  }

  /**
   * Close conversation
   */
  async closeConversation(conversationId: number): Promise<SupportConversation> {
    try {
      const response = await supportApi.post<{ conversation: SupportConversation }>(
        `/conversation/${conversationId}/close`,
        {}
      );
      return response.data.conversation;
    } catch (error: any) {
      console.error('Error closing conversation:', error);
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.message || 'Failed to close conversation');
      }
      throw error;
    }
  }
}

export const supportService = new SupportService();
export default supportService;

